import { motion } from "framer-motion";
import { MapPin, Clock, Zap, Users, ShieldCheck, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { AnimatedSection, fadeInUp } from "./AnimatedSection";

/**
 * LocalSocialProofStrip — honest AU/NZ positioning.
 *
 * No third-party brand names/logos without written consent (AU Competition &
 * Consumer Act — implied endorsement), and no customer counts or ratings we
 * can't evidence yet. Only product facts here. `FEATURED_BRANDS` lights up the
 * logo wall once you hold a documented consent_ref for each entry.
 */
const PRODUCT_FACTS = [
  { icon: Users,       value: "AU & NZ",        label: "Built for Shopify stores here",     tone: "violet"  },
  { icon: Clock,       value: "Weekly",         label: "3–5 fresh ad variants per product", tone: "indigo"  },
  { icon: Zap,         value: "Under a minute", label: "From product link to finished ad",  tone: "fuchsia" },
  { icon: ShieldCheck, value: "No lock-in",     label: "Monthly plans, cancel any time",    tone: "purple"  },
];

const TONE_MAP = {
  violet:  { ring: "ring-violet-400/40",  text: "text-violet-300",  bg: "bg-violet-500/10"  },
  indigo:  { ring: "ring-indigo-400/40",  text: "text-indigo-300",  bg: "bg-indigo-500/10"  },
  fuchsia: { ring: "ring-fuchsia-400/40", text: "text-fuchsia-300", bg: "bg-fuchsia-500/10" },
  purple:  { ring: "ring-purple-400/40",  text: "text-purple-300",  bg: "bg-purple-500/10"  },
};

// Verticals the prompt library and templates are tuned for — not customer counts.
const CATEGORIES = ["Skincare", "Activewear", "Supplements", "Coffee & tea", "Homewares", "Fashion", "Outdoor", "Wines"];

// Populate only with documented consent: { name, logo, url, consent_ref }
const FEATURED_BRANDS = [];

const TRUST_FACTS = ["Built for AU & NZ brands", "Scripts checked for AU/NZ ad rules", "AEST support hours"];

const slug = (s) => s.replace(/\s+/g, "-").toLowerCase().replace(/[^a-z0-9-]/g, "");

const FactCard = ({ icon: Icon, value, label, tone }) => {
  const t = TONE_MAP[tone];
  return (
    <div data-testid={`local-stat-${slug(label)}`} className={`rounded-2xl p-4 md:p-5 glass ring-1 ${t.ring} bg-white/[0.02]`}>
      <div className={`w-9 h-9 rounded-xl ${t.bg} flex items-center justify-center mb-3`}>
        <Icon className={`w-4 h-4 ${t.text}`} />
      </div>
      <p className={`text-xl md:text-2xl font-extrabold ${t.text} leading-tight`}>{value}</p>
      <p className="text-white/55 text-xs md:text-sm mt-1 leading-snug">{label}</p>
    </div>
  );
};

const FeaturedBrandsWall = () => (
  <motion.div variants={fadeInUp} className="mb-10" data-testid="featured-brands-wall">
    <p className="text-white/40 text-[11px] uppercase tracking-widest text-center mb-4">Featured customers</p>
    <div className="grid grid-cols-3 md:grid-cols-6 gap-6 items-center">
      {FEATURED_BRANDS.map((b) => (
        <a key={b.name} href={b.url} target="_blank" rel="noopener noreferrer" aria-label={b.name}
          className="opacity-60 hover:opacity-100 transition-opacity" data-testid={`featured-brand-${slug(b.name)}`}>
          <img src={b.logo} alt={b.name} className="max-h-8 mx-auto grayscale hover:grayscale-0" loading="lazy" />
        </a>
      ))}
    </div>
  </motion.div>
);

const BeFeaturedCta = () => (
  <motion.div variants={fadeInUp} data-testid="be-featured-cta"
    className="mb-10 rounded-2xl p-5 md:p-6 ring-1 ring-white/10 bg-white/[0.02] flex flex-col md:flex-row items-center gap-4 md:gap-6">
    <div className="flex-1 text-center md:text-left">
      <p className="text-white font-bold text-sm md:text-base mb-1">Founder of a Shopify AU/NZ brand?</p>
      <p className="text-white/55 text-xs md:text-sm">
        Ship 3+ ads with Lumora this month and we&apos;ll feature your store on this wall (with your permission, of course).
      </p>
    </div>
    <Link to="/register" data-testid="be-featured-cta-link"
      className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 text-white font-bold text-xs md:text-sm shadow-lg shadow-violet-500/25 whitespace-nowrap">
      Get featured <ArrowRight className="w-3.5 h-3.5" />
    </Link>
  </motion.div>
);

const CategoryChips = () => (
  <motion.div variants={fadeInUp} className="mb-8">
    <p className="text-white/40 text-[11px] uppercase tracking-widest text-center mb-4">Templates tuned for</p>
    <div className="flex flex-wrap items-center justify-center gap-2 md:gap-2.5">
      {CATEGORIES.map((c) => (
        <span key={c} data-testid={`local-category-${slug(c)}`}
          className="inline-flex items-center px-3.5 py-2 rounded-full glass border border-white/10 hover:border-violet-400/40 transition-colors text-white/80 text-xs md:text-sm font-medium">
          {c}
        </span>
      ))}
    </div>
  </motion.div>
);

const TrustRow = () => (
  <motion.div variants={fadeInUp} className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs md:text-sm text-white/45">
    {TRUST_FACTS.map((f, i) => (
      <span key={f} className="inline-flex items-center gap-2">
        {i > 0 && <span className="hidden sm:inline text-white/20 mr-4">·</span>}
        <span className="w-1.5 h-1.5 rounded-full bg-violet-400 animate-pulse" />
        {f}
      </span>
    ))}
  </motion.div>
);

export const LocalSocialProofStrip = () => (
  <AnimatedSection className="py-14 md:py-16 relative" data-testid="local-social-proof">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <motion.div variants={fadeInUp} className="text-center mb-10">
        <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full pill text-sm text-violet-300 mb-4">
          <MapPin className="w-4 h-4" /> Made for Shopify brands across AU &amp; NZ
        </span>
        <h2 className="text-2xl md:text-3xl font-extrabold text-white/95">
          <span className="gradient-text">Built for Aussie &amp; Kiwi founders</span>
        </h2>
        <p className="text-white/50 text-sm md:text-base mt-2 max-w-xl mx-auto">
          From Bondi to Byron to Queenstown — ship weekly ad variants without a Melbourne agency retainer.
        </p>
      </motion.div>
      <motion.div variants={fadeInUp} className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4 mb-10" data-testid="local-social-stats">
        {PRODUCT_FACTS.map((f) => <FactCard key={f.label} {...f} />)}
      </motion.div>
      {FEATURED_BRANDS.length > 0 ? <FeaturedBrandsWall /> : <BeFeaturedCta />}
      <CategoryChips />
      <TrustRow />
    </div>
  </AnimatedSection>
);
